import { useFieldArray, useForm } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { Button, Form, PriceInput } from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { Rate } from 'types/store'
import { cn } from 'utils/style'
import { usePopulateCommissions } from '../../hooks/usePopulateCommissions'
import { useAddCommissionMutation } from '../../mutations/useAddCommissionMutation'
import { useRemoveCommissionMutation } from '../../mutations/useRemoveCommissionMutation'
import { useFetchCommissions } from '../../queries/useFetchCommissions'

const rowClassName = 'grid grid-cols-2 items-end gap-3 sm:grid-cols-[1fr_1fr_auto]'
const buttonClassName =
	'col-span-2 h-10 rounded-md px-4 text-sm font-medium shadow transition-colors hover:bg-primary/90 sm:col-span-1'
const outlineClassName = 'border border-border bg-background text-foreground hover:bg-muted'

export const Commissions = () => {
	const { t } = useTranslation()
	const { commissions, refetchCommissions } = useFetchCommissions()
	const { register: registerCommission, setValue: setCommissionValue } = useForm()
	const {
		register: registerNewCommission,
		control: controlNewCommission,
		getValues: getCommissionsValues
	} = useForm()
	const {
		fields,
		append: appendNewCommission,
		remove: removeNewCommission
	} = useFieldArray({
		name: 'newCommissions',
		control: controlNewCommission
	})

	usePopulateCommissions({
		data: commissions?.data?.results,
		setData: setCommissionValue
	})

	const { removeCommissionMutation, removeCommissionIsLoading } =
		useRemoveCommissionMutation(refetchCommissions)
	const { addCommissionMutation, addCommissionIsLoading } =
		useAddCommissionMutation(removeNewCommission)

	const handleCommissionAddition = (index: number) => {
		addCommissionMutation(getCommissionsValues(`newCommissions[${index}]`))
	}

	return (
		<Form variant="card">
			<p className="text-[12px] leading-relaxed text-muted-foreground">
				{t(
					'Set the commission rate by product price. Products priced at or above the minimal price use that rate.'
				)}
			</p>
			{commissions?.data?.results?.map((rate: Rate, index: number) => (
				<div key={rate.id} className={rowClassName}>
					<PriceInput
						id={`minimal_price_${rate.id}`}
						label={t('Minimal product price')}
						{...registerCommission(`commissions[${index}].minimal_price`)}
						type="number"
						step="0.01"
					/>
					<InputGroup
						id={`rate_${rate.id}`}
						label={t('Commission rate (%)')}
						{...registerCommission(`commissions[${index}].rate`)}
						type="number"
						step="0.01"
					/>
					<Button
						styleType="outline"
						className={cn(buttonClassName, outlineClassName)}
						isLoading={removeCommissionIsLoading}
						onClick={() => removeCommissionMutation(rate.id)}>
						{t('Remove')}
					</Button>
				</div>
			))}
			{fields.map((item, index) => (
				<div key={item.id} className={rowClassName}>
					<PriceInput
						id={`new_minimal_price_${item.id}`}
						label={t('Minimal product price')}
						{...registerNewCommission(`newCommissions[${index}].minimal_price`)}
						type="number"
						step="0.01"
					/>
					<InputGroup
						id={`new_rate_${item.id}`}
						label={t('Commission rate (%)')}
						{...registerNewCommission(`newCommissions[${index}].rate`)}
						type="number"
						step="0.01"
					/>
					<Button
						className={buttonClassName}
						isLoading={addCommissionIsLoading}
						onClick={() => handleCommissionAddition(index)}>
						{t('Add')}
					</Button>
				</div>
			))}
			<Button
				styleType="outline"
				className={cn(
					'h-10 w-full rounded-md border-dashed px-4 text-sm font-medium shadow disabled:cursor-not-allowed disabled:opacity-50',
					outlineClassName
				)}
				disabled={fields.length > 0}
				onClick={() => appendNewCommission({ minimal_price: '', rate: '' })}>
				{t('+ Add new commission rate')}
			</Button>
		</Form>
	)
}
